import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { useAdminAuth } from '../context/AdminAuthContext'
import { DataTable } from '../components/DataTable'
import { Alert, Button, Card, ConfirmAction, EmptyState, PageHeader, Spinner } from '../components/ui'
import { ENTITY_LABELS, formatCurrency } from '../utils/format'

export default function Prices() {
  const { isSuperAdmin } = useAdminAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [tables, setTables] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState(location.state?.message || '')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await adminApi.listPrices()
      setTables(data.data || [])
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const remove = async (id) => {
    try {
      await adminApi.deletePrice(id)
      setMessage('Tabela de preços excluída.')
      load()
    } catch (err) {
      setError(getApiErrorMessage(err))
    }
  }

  if (loading) return <Spinner />

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Comercial" title="Tabelas de preço" description="Faixas de cobrança por pessoas, equipamentos e Módulo Clube." icon="fa-tags" actions={<Button icon="fa-plus" onClick={() => navigate('/prices/new')}>Nova tabela</Button>} />
      {error && <Alert type="error">{error}</Alert>}
      {message && <Alert type="success">{message}</Alert>}

      <div className="grid gap-4 md:grid-cols-2">
        {tables.map((t) => (
          <Card
            key={t.id}
            title={t.name}
            action={
              <div className="flex gap-1">
                <Button variant="outline" className="!px-2 !py-1 text-xs" onClick={() => navigate(`/prices/${t.id}/edit`)}>
                  Editar
                </Button>
                {isSuperAdmin && (
                  <ConfirmAction className="!px-2 !py-1 text-xs" onConfirm={() => remove(t.id)} />
                )}
              </div>
            }
          >
            <p className="text-sm text-slate-600">{t.description || 'Sem descrição'}</p>
            <p className="mt-2 text-xs text-slate-400">{t.ranges?.length || 0} faixa(s)</p>
            <div className="mt-3">
              <DataTable
                columns={[
                  {
                    key: 'entity_type',
                    label: 'Tipo',
                    render: (r) => ENTITY_LABELS[r.entity_type] || r.entity_type,
                  },
                  {
                    key: 'min_quantity',
                    label: 'Faixa',
                    render: (r) =>
                      `${r.min_quantity} – ${r.max_quantity == null ? '∞' : r.max_quantity}`,
                  },
                  {
                    key: 'price',
                    label: 'Preço',
                    render: (r) => formatCurrency(r.price),
                  },
                  {
                    key: 'is_incremental',
                    label: 'Modo',
                    render: (r) => (r.is_incremental ? 'Incremental' : 'Base'),
                  },
                ]}
                rows={t.ranges || []}
                emptyMessage="Nenhuma faixa cadastrada."
              />
            </div>
          </Card>
        ))}
        {!tables.length && <div className="md:col-span-2"><Card><EmptyState title="Nenhuma tabela de preços cadastrada" icon="fa-tags" /></Card></div>}
      </div>
    </div>
  )
}
